"use client"

import clsx from "clsx"
import { useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { safeFilesFromEvent } from "../../utils/safeFilesFromEvent"
import { TIconButton } from "../../ui/button/TIconButton"
import { TIcon } from "../../ui/display/TIcon"
import { TText } from "../../ui/display/TText"
import { CursorFollow, CursorProvider } from "../../ui/shadcn-io/animated-cursor"
import { TTooltip } from "../../ui/tooltip/TTooltip"
import { ImageUploadMenu } from "./image-upload-menu"

interface ImageCardProps {
	index?: number
	value: string
	onChange: (value: string) => void
	onWorkHistorySelect?: () => void | Promise<void>
	label?: string
}

export function ImageCard({ index, value, onChange, onWorkHistorySelect, label }: ImageCardProps) {
	const handleRemove = useCallback(() => {
		onChange("")
	}, [onChange])

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (acceptedFiles.length > 0) {
				const file = acceptedFiles[0]
				const reader = new FileReader()
				reader.onload = (e) => {
					onChange(e.target?.result as string)
				}
				reader.readAsDataURL(file as Blob)
			}
		},
		[onChange]
	)

	const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
		// 임베디드 웹뷰의 FS 핸들 거부(NotAllowedError) 드롭 폴백 — safeFilesFromEvent 주석 참조.
		getFilesFromEvent: safeFilesFromEvent,
		onDrop,
		accept: {
			"image/*": [".png", ".jpg", ".jpeg", ".webp", ".gif"]
		},
		disabled: value !== "",
		noClick: true,
		noDrag: value !== "",
		multiple: false
	})

	return (
		<div {...getRootProps()} className="relative flex flex-col gap-4 w-full h-30 cursor-pointer">
			<input {...getInputProps()} />
			{value ? (
				<div className="relative group w-full h-full rounded-lg overflow-hidden">
					<img src={value} alt="Uploaded" className="absolute inset-0 w-full h-full object-cover blur-lg opacity-80 z-1" />
					<img src={value} alt="Uploaded" className="absolute inset-0 w-full h-full object-contain z-2" />
					<div className="absolute inset-0 bg-g0/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-3">
						<TIconButton name="close" size="xsmall" color="tertiary" className="absolute top-1 right-1" onClick={handleRemove} />
					</div>
				</div>
			) : (
				<ImageUploadMenu onDeviceUpload={open} onWorkHistoryUpload={onWorkHistorySelect}>
					{({ open }) => (
						<TTooltip title="Upload or drag your image">
							<div
								className={clsx(
									"w-full h-30 flex flex-col items-center justify-center gap-2 bg-g800/10 rounded-lg hover:bg-g800/20 hover:inset-ring-1 hover:inset-ring-g800/20 transition-all duration-300",
									open && "bg-g800/20 inset-ring-1 inset-ring-g800/20"
								)}
							>
								<TIcon name="upload" size={24} color="text-secondary" />
								<TText variant="caption" color="secondary" bold>
									{label || (index === 0 ? "Start image" : "End image")}
								</TText>
							</div>
						</TTooltip>
					)}
				</ImageUploadMenu>
			)}
			{isDragActive && (
				<div className="absolute inset-0 flex items-center justify-center border border-dashed border-primary rounded-lg backdrop-blur-[40px] shadow-[0_2_8_0_rgba(0,0,0,0.14)] z-50 bg-g250/60">
					<TText color="primary">Drop files here</TText>
				</div>
			)}
		</div>
	)
}

export function FollowingCursor() {
	return (
		<CursorProvider>
			<CursorFollow sideOffset={16} align="bottom">
				<div className="px-4 py-2 rounded-full text-sm shadow-lg bg-g250/60 border border-g800/20 whitespace-nowrap">Upload or drag your image</div>
			</CursorFollow>
		</CursorProvider>
	)
}
